import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { FaLock, FaEye, FaEyeSlash, FaCheckCircle, FaTimesCircle, FaArrowLeft } from 'react-icons/fa';

const ResetPassword = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);

  // Password validation
  const hasMinLength = password.length >= 8;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const passwordsMatch = password === confirmPassword && password.length > 0;
  const isValid = hasMinLength && hasUppercase && hasLowercase && hasNumber && passwordsMatch;

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-reset-token/${token}`
        );
        setTokenValid(true);
        setEmail(response.data.email);
      } catch (error) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValid) {
      toast.error(
        i18n.language === 'fr' 
          ? 'Veuillez remplir tous les critères du mot de passe' 
          : 'Please meet all password requirements'
      );
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      });
      
      setSuccess(true);
      toast.success(
        i18n.language === 'fr' 
          ? 'Mot de passe réinitialisé avec succès !' 
          : 'Password reset successfully!'
      );
    } catch (error) {
      const message = error.response?.data?.detail || 
        (i18n.language === 'fr' ? 'Erreur lors de la réinitialisation' : 'Error resetting password');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A2540] via-[#0d3a5c] to-[#0A2540] flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardContent className="py-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#635BFF] mx-auto mb-4"></div>
            <p className="text-gray-600">
              {i18n.language === 'fr' ? 'Vérification du lien...' : 'Verifying link...'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid or expired token
  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A2540] via-[#0d3a5c] to-[#0A2540] flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <FaTimesCircle className="text-red-600 text-3xl" />
            </div>
            <CardTitle className="text-2xl text-[#0A2540]">
              {i18n.language === 'fr' ? 'Lien invalide ou expiré' : 'Invalid or expired link'}
            </CardTitle>
            <CardDescription className="text-base mt-2">
              {i18n.language === 'fr' 
                ? 'Ce lien de réinitialisation n\'est plus valide.' 
                : 'This reset link is no longer valid.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Link to="/forgot-password">
              <Button className="w-full bg-[#635BFF] hover:bg-[#5248d9]">
                {i18n.language === 'fr' ? 'Demander un nouveau lien' : 'Request a new link'}
              </Button>
            </Link>
            <Link 
              to="/login" 
              className="block mt-4 text-[#635BFF] hover:underline"
            >
              <FaArrowLeft className="inline mr-2" />
              {i18n.language === 'fr' ? 'Retour à la connexion' : 'Back to login'}
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A2540] via-[#0d3a5c] to-[#0A2540] flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <FaCheckCircle className="text-green-600 text-3xl" />
            </div>
            <CardTitle className="text-2xl text-[#0A2540]">
              {i18n.language === 'fr' ? 'Mot de passe réinitialisé !' : 'Password reset!'}
            </CardTitle>
            <CardDescription className="text-base mt-2">
              {i18n.language === 'fr' 
                ? 'Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.' 
                : 'You can now login with your new password.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button 
              className="w-full bg-[#0A2540] hover:bg-[#0d3a5c] py-6 text-lg"
              onClick={() => navigate('/login')}
            >
              {i18n.language === 'fr' ? 'Se connecter' : 'Login'}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A2540] via-[#0d3a5c] to-[#0A2540] flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl border-0">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-[#635BFF]/10 rounded-full flex items-center justify-center mb-4">
            <FaLock className="text-[#635BFF] text-2xl" />
          </div>
          <CardTitle className="text-2xl text-[#0A2540]">
            {i18n.language === 'fr' ? 'Nouveau mot de passe' : 'New password'}
          </CardTitle>
          <CardDescription className="text-base mt-2">
            {email && (
              <span>
                {i18n.language === 'fr' ? 'Pour le compte : ' : 'For account: '}
                <strong>{email}</strong>
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="password">
                {i18n.language === 'fr' ? 'Nouveau mot de passe' : 'New password'}
              </Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoFocus
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword">
                {i18n.language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm password'}
              </Label>
              <div className="relative mt-1">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            {/* Password requirements */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium text-gray-700 mb-2">
                {i18n.language === 'fr' ? 'Critères du mot de passe :' : 'Password requirements:'}
              </p>
              <div className={`flex items-center text-sm ${hasMinLength ? 'text-green-600' : 'text-gray-500'}`}>
                {hasMinLength ? <FaCheckCircle className="mr-2" /> : <span className="w-4 h-4 border rounded-full mr-2"></span>}
                {i18n.language === 'fr' ? '8 caractères minimum' : 'At least 8 characters'}
              </div>
              <div className={`flex items-center text-sm ${hasUppercase ? 'text-green-600' : 'text-gray-500'}`}>
                {hasUppercase ? <FaCheckCircle className="mr-2" /> : <span className="w-4 h-4 border rounded-full mr-2"></span>}
                {i18n.language === 'fr' ? 'Une majuscule' : 'One uppercase letter'}
              </div>
              <div className={`flex items-center text-sm ${hasLowercase ? 'text-green-600' : 'text-gray-500'}`}>
                {hasLowercase ? <FaCheckCircle className="mr-2" /> : <span className="w-4 h-4 border rounded-full mr-2"></span>}
                {i18n.language === 'fr' ? 'Une minuscule' : 'One lowercase letter'}
              </div>
              <div className={`flex items-center text-sm ${hasNumber ? 'text-green-600' : 'text-gray-500'}`}>
                {hasNumber ? <FaCheckCircle className="mr-2" /> : <span className="w-4 h-4 border rounded-full mr-2"></span>}
                {i18n.language === 'fr' ? 'Un chiffre' : 'One number'}
              </div>
              <div className={`flex items-center text-sm ${passwordsMatch ? 'text-green-600' : 'text-gray-500'}`}>
                {passwordsMatch ? <FaCheckCircle className="mr-2" /> : <span className="w-4 h-4 border rounded-full mr-2"></span>}
                {i18n.language === 'fr' ? 'Les mots de passe correspondent' : 'Passwords match'}
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full bg-[#635BFF] hover:bg-[#5248d9] py-6 text-lg"
              disabled={loading || !isValid}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {i18n.language === 'fr' ? 'Réinitialisation...' : 'Resetting...'}
                </span>
              ) : (
                i18n.language === 'fr' ? 'Réinitialiser le mot de passe' : 'Reset password'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
